import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenu, HiX } from 'react-icons/hi';
import { FaHeart } from 'react-icons/fa';

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/services', label: 'Features' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/blog', label: 'Blog' },
  { path: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          padding: scrolled ? '10px 0' : '20px 0',
          background: scrolled ? 'rgba(15, 10, 8, 0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(224,123,84,0.1)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="animate-heartbeat"
              style={{
                width: 40, height: 40, background: 'var(--gradient-1)',
                borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <FaHeart size={18} />
            </motion.div>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, fontFamily: 'Space Grotesk' }}>
              Maa<span style={{ color: 'var(--primary-light)' }}>Companion</span>
            </span>
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }} className="desktop-nav">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} style={{
                fontSize: '0.95rem', fontWeight: 500,
                color: location.pathname === link.path ? 'var(--primary-light)' : 'var(--gray-light)',
                transition: 'color 0.3s', position: 'relative',
              }}>
                {link.label}
                {location.pathname === link.path && (
                  <motion.div layoutId="activeNav" style={{
                    position: 'absolute', bottom: -8, left: 0, right: 0,
                    height: 3, background: 'var(--gradient-1)', borderRadius: 10,
                  }} />
                )}
              </Link>
            ))}
            <Link to="/contact" className="btn btn-primary" style={{ padding: '10px 24px', fontSize: '0.9rem' }}>
              Get Started
            </Link>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="mobile-toggle"
            style={{ display: 'none', background: 'none', border: 'none', color: 'var(--white)', fontSize: '1.8rem', cursor: 'pointer' }}>
            {mobileOpen ? <HiX /> : <HiMenu />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            style={{
              position: 'fixed', top: 0, right: 0, width: '80%', maxWidth: 350,
              height: '100vh', background: 'var(--dark-light)', zIndex: 999,
              padding: '100px 30px 30px', display: 'flex', flexDirection: 'column', gap: 5,
              borderLeft: '1px solid rgba(224,123,84,0.1)',
            }}
          >
            {navLinks.map((link, i) => (
              <motion.div key={link.path} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}>
                <Link to={link.path} style={{
                  display: 'block', padding: '15px 20px', fontSize: '1.1rem', fontWeight: 500,
                  color: location.pathname === link.path ? 'var(--primary-light)' : 'var(--white)',
                  borderRadius: 'var(--radius-sm)',
                  background: location.pathname === link.path ? 'rgba(224,123,84,0.1)' : 'transparent',
                }}>
                  {link.label}
                </Link>
              </motion.div>
            ))}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
              <Link to="/contact" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}>
                Get Started
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {mobileOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setMobileOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 998 }}
        />
      )}

      <style>{`
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: block !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;